import { openDB, type DBSchema, type IDBPDatabase, type IDBPObjectStore } from "idb";
import { seedCaloriePlan, seedProgram } from "../domain/seed";
import type { AppSettings, CalorieEntry, CaloriePlanVersion, ISODate, SyncMutation, WorkoutSession, ProgramVersion } from "../domain/types";

interface TrainingDB extends DBSchema {
  settings: { key: string; value: AppSettings };
  programs: { key: string; value: ProgramVersion };
  caloriePlans: { key: string; value: CaloriePlanVersion };
  calories: { key: string; value: CalorieEntry; indexes: { byDate: ISODate } };
  workouts: { key: string; value: WorkoutSession; indexes: { byDate: ISODate } };
  syncQueue: { key: string; value: SyncMutation; indexes: { byPushedAt: string } };
}

const DB_NAME = "training-calorie-app";
const SETTINGS_ID = "settings";

let dbPromise: Promise<IDBPDatabase<TrainingDB>> | undefined;

export function db(): Promise<IDBPDatabase<TrainingDB>> {
  dbPromise ??= openDB<TrainingDB>(DB_NAME, 1, {
    upgrade(database) {
      database.createObjectStore("settings", { keyPath: "id" });
      database.createObjectStore("programs", { keyPath: "id" });
      database.createObjectStore("caloriePlans", { keyPath: "id" });
      const calories = database.createObjectStore("calories", { keyPath: "id" });
      calories.createIndex("byDate", "date");
      const workouts = database.createObjectStore("workouts", { keyPath: "id" });
      workouts.createIndex("byDate", "date");
      const syncQueue = database.createObjectStore("syncQueue", { keyPath: "id" });
      syncQueue.createIndex("byPushedAt", "pushedAt");
    }
  });
  return dbPromise;
}

export async function initialize(): Promise<AppSettings> {
  const database = await db();
  const existing = await database.get("settings", SETTINGS_ID);
  if (existing) {
    await refreshSeedData(database);
    return existing;
  }
  const now = new Date().toISOString();
  const settings: AppSettings = {
    id: SETTINGS_ID,
    createdAt: now,
    updatedAt: now,
    revision: 1,
    localStartDate: seedProgram.startsOn,
    cloudEndpoint: "",
    userId: "me",
    deviceId: crypto.randomUUID()
  };
  const tx = database.transaction(["settings", "programs", "caloriePlans"], "readwrite");
  await Promise.all([
    tx.objectStore("settings").put(settings),
    tx.objectStore("programs").put(seedProgram),
    tx.objectStore("caloriePlans").put(seedCaloriePlan),
    tx.done
  ]);
  return settings;
}

async function refreshSeedData(database: IDBPDatabase<TrainingDB>): Promise<void> {
  const currentProgram = await database.get("programs", seedProgram.id);
  const currentCalories = await database.get("caloriePlans", seedCaloriePlan.id);
  const tx = database.transaction(["programs", "caloriePlans"], "readwrite");
  if (!currentProgram || currentProgram.revision < seedProgram.revision) {
    await tx.objectStore("programs").put(seedProgram);
  }
  if (!currentCalories || currentCalories.revision < seedCaloriePlan.revision) {
    await tx.objectStore("caloriePlans").put(seedCaloriePlan);
  }
  await tx.done;
}

export async function getSettings(): Promise<AppSettings> {
  return (await db()).get("settings", SETTINGS_ID) as Promise<AppSettings>;
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  await (await db()).put("settings", bump(settings));
}

export async function allPrograms(): Promise<ProgramVersion[]> {
  return (await db()).getAll("programs");
}

export async function allCaloriePlans(): Promise<CaloriePlanVersion[]> {
  return (await db()).getAll("caloriePlans");
}

export async function caloriesForDate(date: ISODate): Promise<CalorieEntry[]> {
  return (await db()).getAllFromIndex("calories", "byDate", date);
}

export async function workoutForDate(date: ISODate): Promise<WorkoutSession | undefined> {
  return (await db()).getFromIndex("workouts", "byDate", date);
}

export async function putCalorie(entry: CalorieEntry): Promise<void> {
  const database = await db();
  const tx = database.transaction(["calories", "syncQueue"], "readwrite");
  const value = bump(entry);
  await tx.objectStore("calories").put(value);
  await enqueueInTx(tx.objectStore("syncQueue"), "calorieEntry", value.id, value.deletedAt ? "delete" : "upsert", value);
  await tx.done;
}

export async function putWorkout(session: WorkoutSession): Promise<void> {
  const database = await db();
  const tx = database.transaction(["workouts", "syncQueue"], "readwrite");
  const value = bump(session);
  await tx.objectStore("workouts").put(value);
  await enqueueInTx(tx.objectStore("syncQueue"), "workoutSession", value.id, value.deletedAt ? "delete" : "upsert", value);
  await tx.done;
}

export async function allUserData() {
  const database = await db();
  const settings = await getSettings();
  return {
    schemaVersion: 1 as const,
    exportedAt: new Date().toISOString(),
    settings,
    programVersions: await database.getAll("programs"),
    caloriePlans: await database.getAll("caloriePlans"),
    calories: await database.getAll("calories"),
    workouts: await database.getAll("workouts")
  };
}

export async function replaceFromBackup(payload: Awaited<ReturnType<typeof allUserData>>): Promise<void> {
  if (payload.schemaVersion !== 1 || !payload.settings || !Array.isArray(payload.programVersions)) {
    throw new Error("Неверный формат backup.");
  }
  const database = await db();
  const tx = database.transaction(["settings", "programs", "caloriePlans", "calories", "workouts", "syncQueue"], "readwrite");
  await tx.objectStore("settings").clear();
  await tx.objectStore("programs").clear();
  await tx.objectStore("caloriePlans").clear();
  await tx.objectStore("calories").clear();
  await tx.objectStore("workouts").clear();
  await tx.objectStore("syncQueue").clear();
  await tx.objectStore("settings").put(payload.settings);
  await Promise.all(payload.programVersions.map((item) => tx.objectStore("programs").put(item)));
  await Promise.all(payload.caloriePlans.map((item) => tx.objectStore("caloriePlans").put(item)));
  await Promise.all(payload.calories.map((item) => tx.objectStore("calories").put(item)));
  await Promise.all(payload.workouts.map((item) => tx.objectStore("workouts").put(item)));
  await tx.done;
}

export async function pendingMutations(): Promise<SyncMutation[]> {
  return (await db()).getAll("syncQueue");
}

export async function markPushed(ids: string[]): Promise<void> {
  const database = await db();
  const tx = database.transaction("syncQueue", "readwrite");
  await Promise.all(ids.map((id) => tx.objectStore("syncQueue").delete(id)));
  await tx.done;
}

function bump<T extends { updatedAt: string; revision: number }>(entity: T): T {
  return { ...entity, updatedAt: new Date().toISOString(), revision: entity.revision + 1 };
}

async function enqueueInTx(
  store: IDBPObjectStore<TrainingDB, ["calories", "syncQueue"] | ["workouts", "syncQueue"], "syncQueue", "readwrite">,
  entity: SyncMutation["entity"],
  entityId: string,
  operation: SyncMutation["operation"],
  payload: unknown
): Promise<void> {
  const now = new Date().toISOString();
  const mutation: SyncMutation = {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    revision: 1,
    entity,
    entityId,
    operation,
    payload
  };
  await store.put(mutation);
}
